import IconInfoOutline from "@common/components/icons/IconInfoOutline";
import WarningCard from "@common/components/thirdParty/mantine/card/WarningCard";
import ButtonAsText from "@common/components/thirdParty/mantine/text/ButtonAsText";
import { formatPrice, formatQuantity } from "@common/utils/string";
import { NUMBER_FORMAT_PRICE_OPTIONS } from "@common/utils/string/constants";
import UsageGraph from "@components/ui/usage/UsageGraph";
import {
  Divider,
  Flex,
  Stack,
  Text,
  TextProps,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { TrialConversionModal } from "@addons/components/TrialConversionModal";
import { IAddOnSlotProps, IMeteredAddOnDescriptor } from "../types";

function Label() {
  return (
    <Stack spacing="xs">
      <Text size="xs">
        Tokens are chunks of text used by AI models. Usage includes both prompts
        and responses.
      </Text>
    </Stack>
  );
}

function InfoTooltip() {
  const theme = useMantineTheme();

  return (
    <Tooltip label={<Label />} withArrow arrowSize={theme.spacing.xs}>
      <Text size="sm" lh={1} w="14px">
        <IconInfoOutline size={14} color="gray" />
      </Text>
    </Tooltip>
  );
}

export function MetabaseAIUsage({
  addOn: { purchase, subscribed, getUsage, getPricePerUnit },
}: IAddOnSlotProps<IMeteredAddOnDescriptor>) {
  const [opened, { open, close }] = useDisclosure(false);
  const usage = getUsage();
  const pricePerToken = getPricePerUnit();

  if (!subscribed || !usage || pricePerToken === undefined) {
    return null;
  }

  const {
    hasQuota,
    usage: tokens,
    includedUnits: freeTokens,
    includedUnitsUsed: includedTokensUsed,
    billableUnits: billableTokens,
  } = usage;
  const { in_trial } = purchase.subscription;

  const pricePerMillionTokens = pricePerToken * 1_000_000;
  const totalCost = billableTokens * pricePerToken;
  const hasTrialTokenLimitReached =
    in_trial && freeTokens > 0 && tokens >= freeTokens;

  if (freeTokens > 0 && (in_trial || billableTokens === 0)) {
    return (
      <Stack spacing="md">
        <UsageGraph
          hasQuota={hasQuota}
          usage={includedTokensUsed}
          prepaidUnits={freeTokens}
          metricLabel="Included use"
          maxLabel={`${formatQuantity(freeTokens, 1)} tokens`}
        />
        {hasTrialTokenLimitReached ? (
          <>
            <WarningCard withWarningIcon>
              <Text size="sm">
                You&apos;ve exceeded your free trial tokens. To continue using
                Metabase AI Service, please{" "}
                <ButtonAsText component="button" fw={700} onClick={open}>
                  activate your plan
                </ButtonAsText>
                .
              </Text>
            </WarningCard>
            <TrialConversionModal opened={opened} onClose={close} />
          </>
        ) : (
          <Text size="sm">
            Price per token afterward -{" "}
            {formatPrice(pricePerMillionTokens, NUMBER_FORMAT_PRICE_OPTIONS)}{" "}
            per 1M tokens
          </Text>
        )}
      </Stack>
    );
  }

  const textProps: TextProps = {
    size: "sm",
    lh: 1,
    sx: { whiteSpace: "nowrap" },
  };

  const sections = [
    {
      label: <Text {...textProps}>Total tokens</Text>,
      value: formatQuantity(tokens, 1),
    },
    {
      label: (
        <Flex gap="xs">
          <Text {...textProps}>Price per token</Text> <InfoTooltip />
        </Flex>
      ),
      value: (
        <Text {...textProps}>
          {formatPrice(pricePerMillionTokens, NUMBER_FORMAT_PRICE_OPTIONS)} per
          1M tokens
        </Text>
      ),
    },
    {
      label: <Text {...textProps}>Total cost</Text>,
      value: formatPrice(totalCost, NUMBER_FORMAT_PRICE_OPTIONS),
    },
  ];

  return (
    <Stack spacing="lg">
      <Stack spacing="lg">
        <Stack>
          {sections.map(({ label, value }, i) => (
            <Flex gap="md" key={i} align="baseline">
              <Text size="sm" sx={{ whiteSpace: "nowrap" }} lh={1}>
                {label}
              </Text>
              <Divider color="gray.3" w="100%" sx={{ borderStyle: "dotted" }} />
              <Text size="sm" lh={1}>
                {value}
              </Text>
            </Flex>
          ))}
        </Stack>
      </Stack>
    </Stack>
  );
}
